import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  createRezeptgruppe,
  getRezeptgruppen,
  getVistPermissions,
} from '../../../actions';
import { selectIsAdmin } from '../../../helpers/permissions';
import ButtonArea from '../ButtonArea';

import RezeptgruppeForm from './RezeptgruppeForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RezeptgruppeCreate = ({
  pathname,
  createRezeptgruppe,
  getRezeptgruppen,
  rezeptgruppen,
  intl,
  token,
  permissions = null,
  isAdmin,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
  });
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [isRezeptgruppeNameDuplicate, setIsRezeptgruppeNameDuplicate] =
    useState(false);

  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Rezeptgruppe create is admin-only today; Step 11 will disable
  // the screen entirely after Zeelandia's manual cleanup.
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    getRezeptgruppen({ getAll: true });
  }, [getRezeptgruppen]);

  // check for duplicates!
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name') {
      const isDuplicate = rezeptgruppen.some(
        (rezeptgruppe) => rezeptgruppe.name === value,
      );
      setIsRezeptgruppeNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Remove fields with empty strings from the formData
    const cleanedFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    createRezeptgruppe(cleanedFormData)
      .then((result) => {
        const id = result.id;
        if (id) {
          history.push(`/controlpanel/rezeptdatenbank/rezeptgruppen`);
        } else {
          setError(
            'Es ist etwas schiefgelaufen, Rezeptgruppe wurde nicht angelegt.',
          );
        }
      })
      .catch((error) => {
        setError(
          'Es ist etwas schiefgelaufen, Rezeptgruppe wurde nicht angelegt',
        );
        setErrorDetail(error.name + ': ' + error.message);
      });
  };
  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !isAdmin) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper rbd add">
      <Helmet title="Rezeptgruppe Anlegen" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>Rezeptgruppe Anlegen</h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  isAdd={true}
                  handleSubmit={handleSubmit}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/rezeptgruppen'}
                  typeString={'die Rezeptgruppe'}
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <div className="error-message">
                <h2>{error}</h2>
                <p>{errorDetail}</p>
              </div>
            )}
            <div className="rdb add form rezeptgruppen">
              <RezeptgruppeForm
                isRezeptgruppeNameDuplicate={isRezeptgruppeNameDuplicate}
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RezeptgruppeCreate.propTypes = {
  pathname: PropTypes.string.isRequired,
  rezeptgruppen: PropTypes.array.isRequired,
  createRezeptgruppe: PropTypes.func.isRequired,
  getRezeptgruppen: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  isAdmin: PropTypes.bool,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      rezeptgruppen: state.rezeptgruppen.data.rezeptgruppen,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
      isAdmin: selectIsAdmin(state),
    }),
    { createRezeptgruppe, getRezeptgruppen, getVistPermissions },
  ),
)(RezeptgruppeCreate);
